import React, { createContext, useContext, useState, useCallback } from 'react';

const AppDataContext = createContext(null);

const INITIAL_LEADS = [
  {
    id: 'L-001',
    customer: 'ABC Construction Ltd',
    contact: 'John Mwangi',
    mobile: '0712 345 678',
    email: 'john.mwangi@abcconstruction.co.tz',
    address: 'Plot 14, Nyerere Road, Dar es Salaam',
    status: 'new',
    date: '2026-01-15',
    executive: 'John Doe',
    priority: 'high',
    avatar: 'AC',
    notes: 'Interested in bulk cement order for a new residential site.',
  },
  {
    id: 'L-002',
    customer: 'XYZ Developers',
    contact: 'Sarah Mushi',
    mobile: '0713 456 789',
    email: 'sarah.mushi@xyzdevelopers.co.tz',
    address: 'Mikocheni Light Industrial Area, Dar es Salaam',
    status: 'contacted',
    date: '2026-01-14',
    executive: 'Sarah Smith',
    priority: 'medium',
    avatar: 'XD',
    notes: 'Follow up after site visit next week.',
  },
  {
    id: 'L-003',
    customer: 'Mega Construction',
    contact: 'Peter Kilonzo',
    mobile: '0714 567 890',
    email: 'peter.kilonzo@megaconstruction.co.tz',
    address: 'Chang\'ombe Industrial Zone, Dar es Salaam',
    status: 'quoted',
    date: '2026-01-13',
    executive: 'Mike Johnson',
    priority: 'high',
    avatar: 'MC',
    notes: 'Quotation sent, awaiting approval from procurement.',
  },
  {
    id: 'L-004',
    customer: 'Urban Homes Ltd',
    contact: 'Grace Ndungu',
    mobile: '0715 678 901',
    email: 'grace.ndungu@urbanhomes.co.tz',
    address: 'Mbezi Beach, Dar es Salaam',
    status: 'converted',
    date: '2026-01-12',
    executive: 'Jane Wang',
    priority: 'low',
    avatar: 'UH',
    notes: 'Converted to order #O-2201.',
  },
  {
    id: 'L-005',
    customer: 'Coastal Builders',
    contact: 'Hassan Juma',
    mobile: '0716 789 012',
    email: 'hassan.juma@coastalbuilders.co.tz',
    address: 'Kigamboni, Dar es Salaam',
    status: 'lost',
    date: '2026-01-11',
    executive: 'Robert Brown',
    priority: 'medium',
    avatar: 'CB',
    notes: 'Went with a competitor on pricing.',
  },
  {
    id: 'L-006',
    customer: 'Northgate Estates',
    contact: 'Amina Rashid',
    mobile: '0717 890 123',
    email: 'amina.rashid@northgateestates.co.tz',
    address: 'Bagamoyo Road, Dar es Salaam',
    status: 'hold',
    date: '2026-01-10',
    executive: 'John Doe',
    priority: 'low',
    avatar: 'NE',
    notes: 'On hold pending budget approval.',
  },
];

const INITIAL_QUOTATIONS = [
  {
    id: 'Q-1001',
    leadId: 'L-003',
    customer: 'Mega Construction',
    contact: 'Peter Kilonzo',
    email: 'peter.kilonzo@megaconstruction.co.tz',
    status: 'sent',
    createdDate: '2026-01-13',
    validUntil: '2026-02-13',
    items: [
      { id: 'i1', description: 'Portland cement 50kg bag', qty: 500, unitPrice: 12500 },
      { id: 'i2', description: 'Reinforcement steel bar 12mm (per length)', qty: 200, unitPrice: 21000 },
    ],
  },
];

const INITIAL_FOLLOWUPS = [
  {
    id: 'F-2001',
    leadId: 'L-002',
    customer: 'XYZ Developers',
    contact: 'Sarah Mushi',
    dueDate: '2026-01-22',
    time: '10:00',
    type: 'visit',
    notes: 'Site visit to confirm delivery access before quoting.',
    status: 'pending',
  },
];

let quotationSeq = 1002;
let followupSeq = 2002;

export function AppDataProvider({ children }) {
  const [leads, setLeads] = useState(INITIAL_LEADS);
  const [quotations, setQuotations] = useState(INITIAL_QUOTATIONS);
  const [followups, setFollowups] = useState(INITIAL_FOLLOWUPS);
  const [toast, setToast] = useState('');

  const notify = useCallback((message) => {
    setToast(message);
    window.clearTimeout(notify._t);
    notify._t = window.setTimeout(() => setToast(''), 2600);
  }, []);

  const getLead = useCallback((id) => leads.find((l) => l.id === id) || null, [leads]);

  const updateLead = useCallback((updated) => {
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
  }, []);

  const setLeadStatus = useCallback((id, status) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
  }, []);

  const getQuotation = useCallback((id) => quotations.find((q) => q.id === id) || null, [quotations]);

  const getQuotationsForLead = useCallback(
    (leadId) => quotations.filter((q) => q.leadId === leadId),
    [quotations]
  );

  const createQuotation = useCallback((quotation) => {
    const id = `Q-${quotationSeq++}`;
    const record = { ...quotation, id, status: quotation.status || 'draft' };
    setQuotations((prev) => [record, ...prev]);
    return record;
  }, []);

  const updateQuotation = useCallback((updated) => {
    setQuotations((prev) => prev.map((q) => (q.id === updated.id ? updated : q)));
  }, []);

  const setQuotationStatus = useCallback((id, status) => {
    setQuotations((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
  }, []);

  const getFollowupsForLead = useCallback(
    (leadId) => followups.filter((f) => f.leadId === leadId),
    [followups]
  );

  const createFollowup = useCallback((followup) => {
    const id = `F-${followupSeq++}`;
    const record = { ...followup, id, status: 'pending' };
    setFollowups((prev) => [record, ...prev]);
    return record;
  }, []);

  const setFollowupStatus = useCallback((id, status) => {
    setFollowups((prev) => prev.map((f) => (f.id === id ? { ...f, status } : f)));
  }, []);

  const value = {
    leads,
    quotations,
    followups,
    toast,
    notify,
    getLead,
    updateLead,
    setLeadStatus,
    getQuotation,
    getQuotationsForLead,
    createQuotation,
    updateQuotation,
    setQuotationStatus,
    getFollowupsForLead,
    createFollowup,
    setFollowupStatus,
  };

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData must be used within AppDataProvider');
  return ctx;
}